import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControlLabel,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import {
    Actions,
    CharCount,
    DangerZone,
    Description,
    HintList,
    PageWrapper,
    Panel,
    PanelHeader,
    PreviewFrame,
    PreviewPlaceholder,
    SplitLayout,
} from "./BulkEmailPage.elements";
import BulkEmailService, {
    AUDIENCE,
    BODY_MAX_LENGTH,
    SUBJECT_MAX_LENGTH,
} from "@/Service/BulkEmailService";
import { useAuthState } from "@/store";
import { useAlertActions } from "@/store/alertSlice";
import InsertContentDialog from "./emailInserts/InsertContentDialog";
import { insertHtmlAtSelection } from "./emailInserts/emailHtml";

const PREVIEW_DEBOUNCE_MS = 500;

/**
 * Turns an axios failure into something readable. The backend answers with a plain string
 * on its own error paths but with a JSON object for bean-validation failures, and rendering
 * that object directly put a bare "[object Object]" on screen.
 */
const describeError = (error, fallback) => {
    const status = error?.response?.status;
    const data = error?.response?.data;

    if (status === 404) {
        return "Preview endpoint not found — the backend may not have this version deployed yet.";
    }
    if (status === 401 || status === 403) {
        return "Not authorised. This page requires an admin account.";
    }
    if (typeof data === "string" && data.trim()) {
        return data;
    }
    if (data?.message) {
        return data.message;
    }
    if (Array.isArray(data?.errors) && data.errors.length) {
        return data.errors.map((e) => e.defaultMessage || e.message).join(", ");
    }
    if (error?.message) {
        return `${fallback} (${error.message})`;
    }
    return fallback;
};

const STARTER_BODY = `<p>Hi %s,</p>

<p>Here's what's new in the dashboard this month:</p>

<ul>
  <li><strong>Feature one</strong> — what it does for you.</li>
  <li><strong>Feature two</strong> — what it does for you.</li>
</ul>

<p>As always, reply to this email if anything looks off.</p>
`;

const BulkEmailPage = () => {
    const { currentUser } = useAuthState();
    const { showAlert } = useAlertActions();

    const [subject, setSubject] = useState("");
    const [body, setBody] = useState(STARTER_BODY);
    const [testAddress, setTestAddress] = useState("");
    const [previewHtml, setPreviewHtml] = useState("");
    const [previewError, setPreviewError] = useState("");
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [audience, setAudience] = useState(AUDIENCE.ACTIVE_USERS);
    const [recipients, setRecipients] = useState(null);
    const [recipientsError, setRecipientsError] = useState("");
    const [recipientsReload, setRecipientsReload] = useState(0);
    const [isSending, setIsSending] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [insertOpen, setInsertOpen] = useState(false);

    // Guards against an earlier, slower preview response overwriting a newer one.
    const previewRequestId = useRef(0);

    // The body textarea and where its caret was when "Insert" was clicked. Opening the
    // dialog moves focus away, so the selection has to be captured up front and the
    // generated HTML spliced in at that remembered spot.
    const bodyInputRef = useRef(null);
    const insertSelectionRef = useRef({ start: null, end: null });

    const openInsert = useCallback(() => {
        const input = bodyInputRef.current;
        insertSelectionRef.current = input
            ? { start: input.selectionStart, end: input.selectionEnd }
            : { start: null, end: null };
        setInsertOpen(true);
    }, []);

    const handleInsertHtml = useCallback((html) => {
        const { body: nextBody, caret } = insertHtmlAtSelection(
            body,
            insertSelectionRef.current,
            html
        );
        setBody(nextBody);
        setInsertOpen(false);
        // Put the caret right after the inserted block once the dialog has closed and
        // the textarea has re-rendered with the new value.
        setTimeout(() => {
            const input = bodyInputRef.current;
            if (!input) return;
            input.focus();
            try {
                input.setSelectionRange(caret, caret);
            } catch {
                /* some inputs refuse programmatic selection; focus alone is fine */
            }
        }, 0);
    }, [body]);

    const sampleName = currentUser?.name || currentUser?.userName || "Alex";

    useEffect(() => {
        if (currentUser?.email) {
            setTestAddress((current) => current || currentUser.email);
        }
    }, [currentUser]);

    useEffect(() => {
        let cancelled = false;
        setRecipientsError("");
        setRecipients(null);
        BulkEmailService.fetchRecipientCount(audience)
            .then((data) => {
                if (!cancelled) {
                    setRecipients(data);
                    setRecipientsError("");
                }
            })
            .catch((error) => {
                // Only the count is unavailable — the send endpoint is separate and may
                // well be fine, so this must not silently disable the send button.
                console.error("Failed to load recipient count", error);
                if (!cancelled) {
                    setRecipients(null);
                    setRecipientsError(
                        describeError(error, "Could not load the recipient count.")
                    );
                }
            });
        return () => {
            cancelled = true;
        };
    }, [recipientsReload, audience]);

    const subjectTooLong = subject.length > SUBJECT_MAX_LENGTH;
    const bodyTooLong = body.length > BODY_MAX_LENGTH;

    // The preview deliberately does NOT require a subject: an admin pasting HTML wants to
    // see it rendered before writing the subject line, and gating on both made the frame
    // sit empty with a filled body.
    const canPreview = body.trim().length > 0 && !subjectTooLong && !bodyTooLong;
    // Sending is stricter — a real email needs a subject.
    const canSend = canPreview && subject.trim().length > 0;

    // Rendered server-side through email_templates/layout.html, so the frame shows the
    // same HTML the recipients get rather than a lookalike built in the browser.
    useEffect(() => {
        if (!canPreview) {
            setPreviewHtml("");
            setPreviewError("");
            return undefined;
        }

        const requestId = previewRequestId.current + 1;
        previewRequestId.current = requestId;

        const timer = setTimeout(async () => {
            setIsPreviewing(true);
            try {
                const data = await BulkEmailService.previewBulkEmail({
                    subject,
                    body,
                    sampleRecipientName: sampleName,
                });
                if (previewRequestId.current === requestId) {
                    setPreviewHtml(data?.html || "");
                    setPreviewError("");
                }
            } catch (error) {
                console.error("Failed to render email preview", error);
                if (previewRequestId.current === requestId) {
                    setPreviewHtml("");
                    setPreviewError(describeError(error, "Could not render the preview."));
                }
            } finally {
                if (previewRequestId.current === requestId) {
                    setIsPreviewing(false);
                }
            }
        }, PREVIEW_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [subject, body, sampleName, canPreview]);

    const handleSendTest = useCallback(async () => {
        if (!canSend || !testAddress.trim()) return;

        setIsSending(true);
        try {
            await BulkEmailService.sendTestEmail({
                subject,
                body,
                emailAddress: testAddress.trim(),
            });
            showAlert("success", `Test email queued for ${testAddress.trim()}`);
        } catch (error) {
            console.error("Failed to send test email", error);
            showAlert("error", describeError(error, "Failed to send the test email"));
        } finally {
            setIsSending(false);
        }
    }, [canSend, testAddress, subject, body, showAlert]);

    const handleSendAll = useCallback(async () => {
        setConfirmOpen(false);
        setIsSending(true);
        try {
            await BulkEmailService.sendBulkEmailToAll({ subject, body, audience });
            showAlert(
                "success",
                "Send started. It runs in the background and is throttled, so delivery takes a while."
            );
        } catch (error) {
            console.error("Failed to start bulk send", error);
            showAlert("error", describeError(error, "Failed to start the send"));
        } finally {
            setIsSending(false);
        }
    }, [subject, body, audience, showAlert]);

    const isActiveAudience = audience === AUDIENCE.ACTIVE_USERS;

    const recipientLabel = useMemo(() => {
        if (!recipients) return "recipient count loading…";
        const { recipientCount, missingEmailCount, neverSignedInCount } = recipients;
        const base = `${recipientCount} recipient${recipientCount === 1 ? "" : "s"}`;
        if (isActiveAudience) {
            return neverSignedInCount > 0
                ? `${base} (${neverSignedInCount} account${
                      neverSignedInCount === 1 ? "" : "s"
                  } that never signed in are left out)`
                : base;
        }
        return missingEmailCount > 0
            ? `${base} (${missingEmailCount} user${
                  missingEmailCount === 1 ? "" : "s"
              } skipped — no email on record)`
            : base;
    }, [recipients, isActiveAudience]);

    const audienceSentence = isActiveAudience
        ? "Goes to people who have signed in to this dashboard at least once — disabled accounts and legacy users who never moved over are left out"
        : "Goes to every legacy user with an email on record, whether or not they ever used this dashboard";

    return (
        <PageWrapper>
            <Stack spacing={1} sx={{ mb: 3 }}>
                <Typography variant="h5">What's New Email</Typography>
                <Description variant="body2">
                    Compose an announcement in HTML, preview exactly what lands in the
                    inbox, then send it to every contributor. Replaces the manual script.
                </Description>
            </Stack>

            <SplitLayout>
                <Panel>
                    <PanelHeader>
                        <Typography variant="subtitle1">Compose</Typography>
                    </PanelHeader>

                    <Stack spacing={3}>
                        <div>
                            <TextField
                                label="Subject"
                                value={subject}
                                onChange={(event) => setSubject(event.target.value)}
                                placeholder="What's new in the Clippn dashboard"
                                fullWidth
                                error={subjectTooLong}
                            />
                            <Stack
                                direction="row"
                                justifyContent="flex-end"
                                sx={{ mt: 0.5 }}
                            >
                                <CharCount $overLimit={subjectTooLong}>
                                    {subject.length}/{SUBJECT_MAX_LENGTH}
                                </CharCount>
                            </Stack>
                        </div>

                        <div>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ mb: 1 }}
                            >
                                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                    Body (HTML)
                                </Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={openInsert}
                                    data-testid="bulk-email-insert"
                                >
                                    + Insert
                                </Button>
                            </Stack>
                            <TextField
                                label="Body (HTML)"
                                value={body}
                                onChange={(event) => setBody(event.target.value)}
                                inputRef={bodyInputRef}
                                multiline
                                minRows={16}
                                maxRows={30}
                                fullWidth
                                error={bodyTooLong}
                                inputProps={{ spellCheck: false }}
                                sx={{
                                    "& .MuiInputBase-inputMultiline": {
                                        fontFamily:
                                            "ui-monospace, SFMono-Regular, Menlo, monospace",
                                        fontSize: "13px",
                                        lineHeight: 1.5,
                                    },
                                }}
                            />
                            <Stack
                                direction="row"
                                justifyContent="flex-end"
                                sx={{ mt: 0.5 }}
                            >
                                <CharCount $overLimit={bodyTooLong}>
                                    {body.length}/{BODY_MAX_LENGTH}
                                </CharCount>
                            </Stack>

                            <HintList>
                                <li>
                                    Write the body only — the branded header, footer and
                                    dark styling are added around it automatically.
                                </li>
                                <li>
                                    <code>%s</code> is replaced with each recipient&apos;s
                                    name. The preview uses{" "}
                                    <strong>{sampleName}</strong>.
                                </li>
                                <li>
                                    Inline styles only. Mail clients strip{" "}
                                    <code>&lt;style&gt;</code> blocks and JavaScript.
                                </li>
                                <li>
                                    Place the cursor where you want an image, GIF or button
                                    and use <strong>+ Insert</strong>. The generated HTML lands
                                    in the body and stays editable.
                                </li>
                            </HintList>
                        </div>
                    </Stack>
                </Panel>

                <Panel>
                    <PanelHeader>
                        <Typography variant="subtitle1">Preview</Typography>
                        {isPreviewing && (
                            <CharCount>rendering…</CharCount>
                        )}
                    </PanelHeader>

                    {previewHtml ? (
                        <PreviewFrame
                            title="Email preview"
                            srcDoc={previewHtml}
                            // No allow-scripts: the draft is untrusted-ish HTML and email
                            // clients would not run scripts either.
                            sandbox=""
                        />
                    ) : (
                        <PreviewPlaceholder>
                            <Typography variant="body2">
                                {previewError
                                    ? String(previewError)
                                    : "Add some HTML in the body to see the rendered email."}
                            </Typography>
                        </PreviewPlaceholder>
                    )}
                </Panel>
            </SplitLayout>

            <Panel style={{ marginTop: 24 }}>
                <PanelHeader>
                    <Typography variant="subtitle1">Send a test first</Typography>
                </PanelHeader>

                <Actions direction="row" spacing={2} alignItems="flex-start">
                    <TextField
                        label="Test recipient"
                        value={testAddress}
                        onChange={(event) => setTestAddress(event.target.value)}
                        placeholder="you@clippn.com"
                        sx={{ minWidth: 280, flex: "1 1 280px" }}
                    />
                    <Button
                        variant="outlined"
                        onClick={handleSendTest}
                        disabled={!canSend || !testAddress.trim() || isSending}
                        sx={{ mt: 1 }}
                    >
                        Send test
                    </Button>
                </Actions>
            </Panel>

            <DangerZone>
                <Typography variant="subtitle1" gutterBottom>
                    Send to everyone
                </Typography>
                <RadioGroup
                    value={audience}
                    onChange={(e) => setAudience(e.target.value)}
                    name="bulk-email-audience"
                    sx={{ mb: 1 }}
                >
                    <FormControlLabel
                        value={AUDIENCE.ACTIVE_USERS}
                        control={<Radio size="small" color="error" />}
                        label="Active users — signed in to this dashboard at least once (recommended)"
                        data-testid="bulk-email-audience-active"
                    />
                    <FormControlLabel
                        value={AUDIENCE.LEGACY_ALL}
                        control={<Radio size="small" color="error" />}
                        label="All legacy users — everyone with an email on record, incl. people who never used this app"
                        data-testid="bulk-email-audience-legacy"
                    />
                </RadioGroup>
                <Description variant="body2" data-testid="bulk-email-audience-summary">
                    {audienceSentence} — {recipientLabel}. There is no undo and no unsend.
                </Description>

                {recipientsError && (
                    <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        sx={{ mt: 1.5, flexWrap: "wrap" }}
                    >
                        <Typography variant="body2" sx={{ color: "#e5484d" }}>
                            {recipientsError} The send below still works — you just
                            won&apos;t see how many people it reaches.
                        </Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setRecipientsReload((n) => n + 1)}
                        >
                            Retry
                        </Button>
                    </Stack>
                )}

                <Divider sx={{ my: 2, borderColor: "rgba(229, 72, 77, 0.25)" }} />

                <Button
                    variant="contained"
                    color="error"
                    onClick={() => setConfirmOpen(true)}
                    disabled={!canSend || isSending}
                >
                    Send to all contributors
                </Button>
            </DangerZone>

            <InsertContentDialog
                open={insertOpen}
                onClose={() => setInsertOpen(false)}
                onInsert={handleInsertHtml}
            />

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Send this email to everyone?</DialogTitle>
                <DialogContent>
                    <DialogContentText component="div">
                        <p>
                            {recipients?.recipientCount != null ? (
                                <>
                                    <strong>{recipients.recipientCount}</strong>{" "}
                                    {isActiveAudience
                                        ? "people who have signed in to this dashboard"
                                        : "legacy users"}{" "}
                                    will receive &ldquo;{subject}&rdquo;.
                                </>
                            ) : (
                                <>
                                    {isActiveAudience
                                        ? "Everyone who has signed in to this dashboard"
                                        : "Every legacy user with an email on record"}{" "}
                                    will receive &ldquo;{subject}&rdquo;.{" "}
                                    <strong>
                                        The recipient count could not be loaded, so the
                                        exact number is unknown.
                                    </strong>
                                </>
                            )}
                        </p>
                        <p>
                            Sending is throttled to avoid provider rate limits, so it runs
                            in the background for a while after you confirm. It cannot be
                            cancelled from here.
                        </p>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={handleSendAll}>
                        Send to all
                    </Button>
                </DialogActions>
            </Dialog>
        </PageWrapper>
    );
};

export default BulkEmailPage;
